import React, { ReactElement } from 'react'

import { useDebouncedCallback } from 'use-debounce'
import styled, { css } from 'styled-components'

import { variables } from 'src/Styled/variables'
import Loader from 'src/Assets/Svg/loader.svg?react'
import { DEBOUNCE_DELAY_100 } from 'src/Constants/constants'

export interface PrimaryButtonProps extends React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> {
  loading?: boolean
}

export const secondaryButtonStyles = css<{ $loading?: boolean }>`
  background: ${({ theme }) => theme.color.hoverWhite};
  border: ${({ theme }) => `1px solid ${theme.color.lightGrey}`};
  color: ${({ theme }) => theme.color.darkGrey};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.color.lightGrey};
    border-color: ${({ theme }) => theme.color.lightGrey};
  }

  &:focus-visible {
    outline: none;
    background: ${({ theme }) => theme.color.hoverWhite};
    border-color: ${({ theme }) => theme.color.darkGreen};
    color: ${({ theme }) => theme.color.darkGreen};
  }

  &:active {
    outline: none;
    background: ${({ theme }) => theme.color.hoverWhite};
    border-color: ${({ theme }) => theme.color.lightGrey};
    color: ${({ theme }) => theme.color.mediumGrey};

    svg {
      path {
        fill: ${({ theme }) => theme.color.mediumGrey};
      }
    }
  }

  ${({ $loading }) => {
    if (!$loading) {
      return css`
        &:disabled {
          cursor: not-allowed;
          background: ${({ theme }) => theme.color.hoverWhite};
          border-color: ${({ theme }) => theme.color.lightGrey};
          color: ${({ theme }) => theme.color.lightGrey};

          svg {
            path {
              fill: ${({ theme }) => theme.color.lightGrey};
            }
          }
        }
      `
    }

    return css`
      &,
      &:hover {
        background: ${({ theme }) => theme.color.hoverWhite};
        border-color: ${({ theme }) => theme.color.lightGrey};
      }

      svg {
        path {
          fill: ${({ theme }) => theme.color.slateGrey};
        }
      }
    `
  }}
`

export const StyledButton = styled.button<{
  $loading?: boolean
}>`
  position: relative;
  display: flex;
  height: 48px;
  padding: 8px 40px;
  justify-content: center;
  align-items: center;
  border-radius: 40px;
  gap: 8px;
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;

  ${secondaryButtonStyles}
`

const LoaderWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  svg {
    width: 17px;
    height: 17px;
  }
`

const ChildrenWrapper = styled.div<{ $loading?: boolean }>`
  opacity: ${({ $loading }) => ($loading ? 0 : 1)};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`

export const PrimaryButton: React.FC<PrimaryButtonProps> = (
  props,
): ReactElement => {
  const { children, loading, onClick, disabled, ...restProps } = props

  const debouncedOnClick = useDebouncedCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (typeof onClick === 'function') {
        onClick(event)
      }
    },
    DEBOUNCE_DELAY_100,
  )
  return (
    <StyledButton
      {...restProps}
      $loading={loading && !disabled}
      onClick={(e) => debouncedOnClick(e)}
      disabled={disabled || loading}
    >
      {loading && !disabled && (
        <LoaderWrapper>
          <Loader data-testid='loader' />
        </LoaderWrapper>
      )}

      <ChildrenWrapper $loading={loading}>{children}</ChildrenWrapper>
    </StyledButton>
  )
}
